// Mirror of apps/backend/src/utils/signing.ts. Produces the same HMAC-SHA256
// signature so the server can verify. Uses Web Crypto — no Node polyfills.

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function sha256(input: string): Promise<ArrayBuffer> {
  const data = new TextEncoder().encode(input);
  return await crypto.subtle.digest('SHA-256', data);
}

async function sha256Hex(input: string): Promise<string> {
  const buf = await sha256(input);
  return toHex(new Uint8Array(buf));
}

export async function deriveSigningKey(accessToken: string): Promise<CryptoKey> {
  const keyMaterial = await sha256(`sig:${accessToken}`);
  return await crypto.subtle.importKey(
    'raw',
    keyMaterial,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
}

export async function computeSignature(
  key: CryptoKey,
  method: string,
  path: string,
  timestampMs: string,
  nonce: string,
  rawBody: string,
): Promise<string> {
  const bodyHash = await sha256Hex(rawBody ?? '');
  const canonical = [method.toUpperCase(), path, timestampMs, nonce, bodyHash].join('\n');
  const sig = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(canonical));
  return toHex(new Uint8Array(sig));
}

export function generateNonce(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  // Web-safe base64 without padding.
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export function generateIdempotencyKey(): string {
  return crypto.randomUUID();
}
